/** Pure helpers for FFA presentation combat trails (no Phaser). */

import { visualTravelMs } from "../../../vfx/attack-fx";
import {
  coneTelegraphPoints as coneTelegraphPointsEng,
  hitPresetId as hitPresetIdEng,
  hitVfxDeferredToAttack as hitVfxDeferredToAttackEng,
  isArtilleryRole as isArtilleryRoleEng,
  resolveAttackOrigin as resolveAttackOriginEng,
  type Pt,
} from "../../../vfx/combat-presentation";

export { lerp2 } from "../../../vfx/attack-fx";
export type { Pt };

/** FFA catalog roles that use artillery shell travel / VFX. */
export const FFA_ARTILLERY_ROLES = ["weapon"] as const;

/** FFA roles whose Hit particle waits for Attack travel. */
export const FFA_DEFERRED_HIT_ROLES = ["chaser", "weapon"] as const;

/**
 * Legacy distance→ms clamp for trails without AttackFxRoleConfig.
 * Prefer `attackTravelSec` + catalog swing when role FX tables exist.
 */
export function projectileTravelMs(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  return visualTravelMs(x0, y0, x1, y1, { kind: "bolt" });
}

/** Slower, heavier shell travel for artillery. */
export function artilleryTravelMs(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): number {
  return visualTravelMs(x0, y0, x1, y1, { kind: "artillery" });
}

/** Resolve muzzle (or keep body center when no rig). */
export function resolveAttackOrigin(
  x0: number,
  y0: number,
  muzzle: { x: number; y: number } | null | undefined,
): { x: number; y: number } {
  return resolveAttackOriginEng(x0, y0, muzzle);
}

/** Ranged visual travel ms (artillery slower). */
export function rangedTravelMs(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  artillery: boolean,
  catalogProjectileMs?: number,
): number {
  if (artillery) return artilleryTravelMs(x0, y0, x1, y1);
  if (catalogProjectileMs != null && catalogProjectileMs > 0) {
    return catalogProjectileMs;
  }
  return projectileTravelMs(x0, y0, x1, y1);
}

/** Hit particle VFX is deferred to Attack travel for ranged roles. */
export function hitVfxDeferredToAttack(role: string, ranged = false): boolean {
  return hitVfxDeferredToAttackEng(role, {
    ranged,
    deferredRoles: FFA_DEFERRED_HIT_ROLES,
  });
}

/** Map attack style / hit mode → VFX preset id. */
export function hitPresetId(style: string, mode: string): string {
  return hitPresetIdEng(style, mode);
}

/** Fan points for a cone telegraph (origin + arc). */
export function coneTelegraphPoints(
  x: number,
  y: number,
  aimAngle: number,
  range: number,
  coneDeg: number,
  segments = 8,
): Pt[] {
  return coneTelegraphPointsEng(x, y, aimAngle, range, coneDeg, segments);
}

/** Whether role should use artillery shell VFX. */
export function isArtilleryRole(role: string): boolean {
  return isArtilleryRoleEng(role, FFA_ARTILLERY_ROLES);
}
